// ─── Link elements on the feedback canvas ────────────────────────
//
// A pasted URL becomes a card showing what is at the other end. Everything here
// is pure and DOM-free so the unit runner can cover it — which matters more than
// usual, because the SSRF guard below is a security property, not a convenience.
// (f-01893)
/** The card's intrinsic size. It has exactly one, and the element's stored width
 *  scales the whole thing — text, icon and image together — the way resizing a
 *  screenshot does. So the card's own CSS never has to be responsive. */
export const FEEDBACK_LINK_W = 344;
export const FEEDBACK_LINK_H = 108;
export const FEEDBACK_LINK_ASPECT = FEEDBACK_LINK_H / FEEDBACK_LINK_W;
/** How much of a user-supplied link title is kept. Shared so the clipboard decode
 *  and the server's own clamp cannot drift — a clip promising more than the create
 *  stores would silently lose the tail of a pasted title. (b-01908) */
export const FEEDBACK_LINK_TITLE_MAX = 300;
/** A link's height is DERIVED, never stored — the same discipline f-01847 applies
 *  to a screenshot's. One number in the database, one shape on the board. */
export function linkCardHeight(w) {
    return Math.round(w * FEEDBACK_LINK_ASPECT);
}
/**
 * The card's MAIN line. The page's own title heads the card when it gave one, the
 * user's when it did not, and the host when neither exists — never empty, so a card
 * is never a blank box.
 *
 * The page wins the top line and the user's title becomes the subtitle beneath it,
 * because the two answer different questions: what this page calls itself, and what
 * WE call it. With only one of them there is nothing to distinguish, so it takes the
 * main line alone. (f-01903)
 */
export function linkCardTitle(link) {
    const found = link.title?.trim();
    if (found)
        return found;
    const own = link.titleOverride?.trim();
    if (own)
        return own;
    return parseHttpUrl(link.url)?.host ?? link.url;
}
/** The card's SUBTITLE: the user's own title, and only where the page supplied one
 *  too — otherwise it is already the main line, and printing it twice is noise.
 *  Null means this card has no second line. (f-01903) */
export function linkCardSubtitle(link) {
    const own = link.titleOverride?.trim();
    if (!own)
        return null;
    return link.title?.trim() ? own : null;
}
export function parseHttpUrl(raw) {
    const m = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/([^/?#]*)([^?#]*)(\?[^#]*)?(#.*)?$/.exec(raw.trim());
    if (!m)
        return null;
    const scheme = m[1].toLowerCase();
    if (scheme !== 'http' && scheme !== 'https')
        return null;
    let authority = m[2];
    // Credentials in a URL are not something this feature carries around.
    const at = authority.lastIndexOf('@');
    if (at >= 0)
        authority = authority.slice(at + 1);
    if (!authority)
        return null;
    let host = authority;
    let port = '';
    if (authority.startsWith('[')) { // [::1]:8080
        const close = authority.indexOf(']');
        if (close < 0)
            return null;
        host = authority.slice(0, close + 1);
        const rest = authority.slice(close + 1);
        if (rest.startsWith(':'))
            port = rest.slice(1);
        else if (rest)
            return null;
    }
    else {
        const colon = authority.indexOf(':');
        if (colon >= 0) {
            host = authority.slice(0, colon);
            port = authority.slice(colon + 1);
        }
    }
    if (!host)
        return null;
    if (port && !/^\d+$/.test(port))
        return null;
    if ((scheme === 'https' && port === '443') || (scheme === 'http' && port === '80'))
        port = '';
    return {
        scheme,
        host: host.toLowerCase(),
        port,
        path: m[3] || '/',
        query: m[4] ?? '',
    };
}
export function formatUrl(u) {
    return `${u.scheme}://${u.host}${u.port ? `:${u.port}` : ''}${u.path}${u.query}`;
}
/** Resolve a possibly-relative reference against an absolute http(s) base.
 *  Enough for an og:image, which is all it is used for. */
export function resolveAgainst(base, ref) {
    const r = ref.trim();
    if (!r)
        return null;
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(r)) {
        // Absolute — and only http(s) survives, so a `javascript:` og:image is
        // dropped rather than carried onto a clickable card.
        const abs = parseHttpUrl(r);
        return abs ? formatUrl(abs) : null;
    }
    const origin = `${base.scheme}://${base.host}${base.port ? `:${base.port}` : ''}`;
    if (r.startsWith('//'))
        return parseHttpUrl(`${base.scheme}:${r}`) ? `${base.scheme}:${r}` : null;
    if (r.startsWith('/'))
        return `${origin}${r}`;
    const dir = base.path.slice(0, base.path.lastIndexOf('/') + 1);
    return `${origin}${dir}${r}`;
}
// ─── what counts as a pasted link ────────────────────────────────
/**
 * The clipboard text, if it is a URL we will make a card out of — else null.
 *
 * Deliberately strict. It must be the WHOLE of what was pasted, because a URL
 * inside a sentence is text somebody copied, not a link they are placing. And
 * only http/https: `javascript:` and `data:` would be an XSS vector the moment
 * the card is clickable, and nothing else has any business on a shared board.
 */
export function parseLinkPaste(text) {
    const s = (text ?? '').trim();
    if (!s || /\s/.test(s))
        return null;
    return parseHttpUrl(s) ? s : null;
}
/**
 * The form of a URL the unfurl cache is keyed on.
 *
 * Two spellings of the same page must not cost two fetches and two stored
 * images: the fragment never reaches the server at all, the host is
 * case-insensitive, and a default port is not a different page. The query is
 * kept — it usually IS the page.
 */
export function normaliseLinkUrl(raw) {
    const u = parseHttpUrl(raw);
    // parseHttpUrl already drops the fragment, lowercases the host and strips a
    // default port — the three ways one page grows two cache rows.
    return u ? formatUrl(u) : raw.trim();
}
const ENTITIES = {
    amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'", nbsp: ' ',
};
/** Just enough entity decoding for a title or description. Not a parser: these
 *  values land in text nodes, never in markup. */
function decodeEntities(s) {
    return s.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (whole, name) => {
        const known = ENTITIES[name.toLowerCase()];
        if (known)
            return known;
        if (name[0] === '#') {
            const n = name[1] === 'x' || name[1] === 'X'
                ? parseInt(name.slice(2), 16)
                : parseInt(name.slice(1), 10);
            return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : whole;
        }
        return whole;
    });
}
function metaContent(html, attr, key) {
    // Both attribute orders, both quote styles — real pages use all of them.
    const pat = new RegExp(`<meta[^>]+(?:${attr}=["']${key}["'][^>]*content=["']([^"']*)["']`
        + `|content=["']([^"']*)["'][^>]*${attr}=["']${key}["'])`, 'i');
    const m = pat.exec(html);
    const v = (m?.[1] ?? m?.[2] ?? '').trim();
    return v ? decodeEntities(v) : null;
}
/**
 * What a page says about itself. `null` fields mean the page did not say —
 * never empty string, which would render as a blank line on the card.
 *
 * Open Graph is preferred over the document's own title because it is what the
 * page chose to be shared as.
 */
export function unfurlFromHtml(html, baseUrl) {
    const titleTag = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(html)?.[1]?.trim();
    const rawImage = metaContent(html, 'property', 'og:image')
        ?? metaContent(html, 'name', 'twitter:image');
    // A preview we would never be allowed to fetch is not a preview.
    const base = parseHttpUrl(baseUrl);
    const image = rawImage && base ? resolveAgainst(base, rawImage) : null;
    return {
        title: metaContent(html, 'property', 'og:title')
            ?? (titleTag ? decodeEntities(titleTag) : null),
        description: metaContent(html, 'property', 'og:description')
            ?? metaContent(html, 'name', 'description'),
        siteName: metaContent(html, 'property', 'og:site_name'),
        image,
    };
}
// ─── the SSRF guard ──────────────────────────────────────────────
/** Parse a dotted-quad into its four octets, or null. */
function v4(s) {
    const parts = s.split('.');
    if (parts.length !== 4)
        return null;
    const out = [];
    for (const p of parts) {
        if (!/^\d{1,3}$/.test(p))
            return null;
        const n = Number(p);
        if (n > 255)
            return null;
        out.push(n);
    }
    return out;
}
function blockedV4(o) {
    const [a, b] = o;
    if (a === 0)
        return true; // 0.0.0.0/8, "this network"
    if (a === 10)
        return true; // private
    if (a === 127)
        return true; // loopback
    if (a === 169 && b === 254)
        return true; // link-local, incl. cloud metadata
    if (a === 172 && b >= 16 && b <= 31)
        return true; // private — 16..31 ONLY
    if (a === 192 && b === 168)
        return true; // private
    if (a === 100 && b >= 64 && b <= 127)
        return true; // CGNAT
    if (a === 192 && b === 0)
        return true; // 192.0.0.0/24 + TEST-NET-1
    if (a >= 224)
        return true; // multicast + reserved + broadcast
    return false;
}
/**
 * Should the unfurl refuse to talk to this address?
 *
 * Checked against the RESOLVED address rather than the hostname, and re-checked
 * after every redirect — a name that resolves publicly once can resolve to
 * 169.254.169.254 the second time, which is the whole trick.
 *
 * Unparseable input is REFUSED, not guessed at: this is the one place where
 * failing closed costs a link preview and failing open costs the network.
 */
export function isBlockedUnfurlAddress(ip) {
    const s = (ip ?? '').trim().toLowerCase();
    if (!s)
        return true;
    const asV4 = v4(s);
    if (asV4)
        return blockedV4(asV4);
    if (!s.includes(':'))
        return true; // not an address at all
    // v4-mapped (::ffff:127.0.0.1) is the classic bypass: judge it as the v4
    // address it actually reaches.
    const mapped = /^::ffff:(\d+\.\d+\.\d+\.\d+)$/.exec(s);
    if (mapped) {
        const o = v4(mapped[1]);
        return o ? blockedV4(o) : true;
    }
    if (s === '::' || s === '::1')
        return true; // unspecified, loopback
    if (/^f[cd]/.test(s))
        return true; // fc00::/7 unique-local
    if (/^fe[89ab]/.test(s))
        return true; // fe80::/10 link-local
    if (/^ff/.test(s))
        return true; // multicast
    // Anything left must at least look like a v6 address.
    return !/^[0-9a-f:]+$/.test(s);
}
//# sourceMappingURL=feedback-link.js.map